import { isEqual } from 'lodash';

import { EMPTY_SAVE_STEP_COUNTS } from '@/types/saveSteps.types';

import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { SaveStepCounts } from '@/types/saveSteps.types';

/**
 * Diffs the live `ads` array against the last-saved baseline to produce the
 * Creatives save-step counts. Unlike venues/zones, `AdInterface` carries no
 * `isModified` flag, so each current ad is matched to its baseline
 * counterpart by `_id`:
 * - no baseline match, not deleted -> created
 * - no baseline match, deleted -> added and removed again in this session, counts as neither
 * - baseline match, now deleted (baseline wasn't) -> removed
 * - baseline match, not deleted, differs from baseline -> updated
 * - baseline match, unchanged -> neither
 * - baseline item missing entirely from `currentAds` (defensive) -> removed
 */
export const countAdChanges = (
  currentAds: AdInterface<string>[],
  baselineAds: AdInterface<string>[]
): SaveStepCounts => {
  const matchedBaselineIds = new Set<string>();

  const counts = currentAds.reduce((acc, ad) => {
    const baselineAd = ad._id ? baselineAds.find(b => b._id === ad._id) : undefined;
    if (!baselineAd) {
      return ad.deleted ? acc : { ...acc, created: acc.created + 1 };
    }
    matchedBaselineIds.add(baselineAd._id!);
    if (ad.deleted && !baselineAd.deleted) {
      return { ...acc, removed: acc.removed + 1 };
    }
    if (!ad.deleted && !baselineAd.deleted && !isEqual(ad, baselineAd)) {
      return { ...acc, updated: acc.updated + 1 };
    }
    return acc;
  }, structuredClone(EMPTY_SAVE_STEP_COUNTS));

  const missingFromCurrentCount = baselineAds.filter(b => b._id && !b.deleted && !matchedBaselineIds.has(b._id)).length;

  return {
    ...counts,
    removed: counts.removed + missingFromCurrentCount
  };
};
